import { BACKDROPS, CLASSES, creatureLook, PALETTE, type AvatarCharacter } from "@eldrun/content";
import { Color, Mesh, PerspectiveCamera, PlaneGeometry, Quaternion, Scene, Vector3, type BufferGeometry, type WebGLRenderer } from "three";
import { CREATURE_MODELS } from "../assets/creatures.generated.ts";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { CreatureSkins, isAnimatedType, type SkinnedCreature } from "../game/creature-skins.ts";
import { heldMesh } from "../game/held-item.ts";
import { SkinnedAvatar } from "../game/skinned-avatar.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { EnergyEvents } from "../render/energy-events.ts";
import type { AvatarLibrary } from "../render/avatar-library.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { createSlotRing } from "../render/shaders/slot-ring.ts";
import { NeonMaterials, unindexed } from "../render/style/neon.ts";
import { SkillVfx } from "../game/skill-vfx.ts";
import { Shards } from "../game/shards.ts";
import { NOCK, sampleArrowRibbon } from "../game/arrows.ts";
import { Ribbon, RIBBON_SAMPLES, ribbonLook, swingEnds, type RibbonLook } from "../game/ribbons.ts";
import { SKILL_SHAPES } from "../render/geometry/skill-shapes.ts";
import { createSkillMeshMaterial } from "../render/shaders/skill-mesh.ts";

export interface NeonShowcaseOptions {
  /** Clipe dos avatares e das criaturas (`parado` ou `golpe`). */
  clip: "parado" | "golpe";
  /** Instante fixo (s), para capturas; sem ele, em laço. */
  time: number | undefined;
  /**
   * Galeria das peças das habilidades (F78, `&habilidades=1`): Bastião, Salto, Fênix, Rompe-Núcleo e a coluna do
   * reviver. Desde a F92, também as fitas: o golpe do Cavaleiro e do Bárbaro e uma flecha na cor do Patrulheiro.
   */
  skills?: boolean;
  /**
   * Câmera na altura da flecha da galeria (F92): de lado (`&vista=flecha`, onde a fita deitada sumia de perfil)
   * ou logo atrás de onde ela sai (`&vista=atirador`, como quem atira a vê).
   */
  arrowView?: "lado" | "atirador" | undefined;
}

export interface NeonShowcase {
  stats(): Record<string, unknown>;
  stop(): void;
}

/** A arma da key art de cada personagem das classes (a Arcanista ganha o cajado na F66). */
const WEAPONS: Partial<Record<AvatarCharacter, string>> = { cavaleiro: "espada-prata", barbaro: "lanca-prata", patrulheiro: "arco-prata" };

/** Personagens que golpeiam com a fita na galeria das habilidades (F92); o arco do Patrulheiro solta a flecha. */
const SWINGERS: readonly AvatarCharacter[] = ["cavaleiro", "barbaro"];
/** O laço do golpe na vitrine (s) e o voo da flecha do Patrulheiro, que cruza a frente da cena da esquerda para a direita. */
const SWING_LOOP = 1.2;
const ARROW = { loop: 1.5, flight: 0.6, from: new Vector3(-5, 1.7, -2.5), velocity: new Vector3(16, 2, 2), gravity: 9.8 } as const;

/** Os monstros (laranja) e o chefe (vermelho) da cena, com onde ficam. */
const CAST = [
  { type: "mapinguari", x: 0, z: 9, boss: true },
  { type: "corpo-seco", x: -4.5, z: 5, boss: false },
  { type: "corpo-seco", x: 4.5, z: 5, boss: false },
  { type: "lobisomem", x: -2.2, z: 6.5, boss: false },
  { type: "lobisomem", x: 2.2, z: 6.5, boss: false },
] as const;

/** Chão low-poly: um plano subdividido com ondulação leve (o terreno de verdade usa a malha analítica, F59). */
function groundGeometry(): BufferGeometry {
  const plane = new PlaneGeometry(48, 48, 28, 28);
  plane.rotateX(-Math.PI / 2);
  const pos = plane.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    pos.setY(i, Math.sin(x * 0.35) * 0.18 + Math.sin(z * 0.27 + 1.3) * 0.14 - (Math.hypot(x, z) < 12 ? 0 : (Math.hypot(x, z) - 12) * 0.05));
  }
  return unindexed(plane);
}

/**
 * Vitrine da malha monocromática com neon (`?debug=neon&clipe=parado|golpe&instante=&pos=`,
 * F58, sem servidor): os 4 personagens das classes na cor de cada uma (F85), monstros em laranja e o Ciclope em
 * vermelho, num chão low-poly. É o portão visual da Fase 8 (doc 09, ADR 0014) e a medição de custo.
 */
export async function startNeonShowcase(renderer: WebGLRenderer, avatars: AvatarLibrary, models: ModelLibrary, options: NeonShowcaseOptions): Promise<NeonShowcase> {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(0.5);
  scene.background = new Color(BACKDROPS.painel);
  const materials = new NeonMaterials(atmosphere);
  const camera = new PerspectiveCamera(42, 1, 0.1, 300);

  const ground = new Mesh(groundGeometry(), materials.get("terreno", PALETTE.neutro.hex));
  scene.add(ground);

  // Fitas da galeria (F92): quem golpeia, com o item na mão e a cor da classe.
  const swingers: { avatar: SkinnedAvatar; held: Mesh; ribbon: Ribbon; look: RibbonLook }[] = [];
  // Só os personagens que chegaram (F88: um arquivo pode faltar).
  const heroes = CLASSES.filter(({ character }) => avatars.has(character)).map(({ id, character }, slot) => {
    const hex = PALETTE.classes[id].hex;
    const avatar = new SkinnedAvatar(avatars, atmosphere, character, hex, slot);
    avatar.root.position.set((slot - 1.5) * 1.6, 0, 0);
    avatar.root.rotation.y = Math.PI;
    const weapon = WEAPONS[character];
    const held = weapon ? heldMesh(weapon, models, materials.get("arma-heroi", hex), "kaykit") : undefined;
    if (weapon) avatar.setHeld(held, weapon);
    if (options.skills && held && SWINGERS.includes(character)) {
      const ribbon = new Ribbon();
      scene.add(ribbon.mesh);
      swingers.push({ avatar, held, ribbon, look: ribbonLook("golpe", hex, 1, false) });
    }
    // O corpo fica na superfície dos atores (F119, a da partida); a arma, na malha neon.
    avatar.root.traverse((o) => {
      const mesh = o as Mesh;
      if (mesh.isMesh && mesh.name !== character) {
        mesh.geometry = unindexed(mesh.geometry);
        mesh.material = materials.get("arma-heroi", hex);
      }
    });
    // Anel sob os pés (F63): a cor da classe e o número do jogador em marcas (depois da troca de materiais acima).
    avatar.root.add(createSlotRing(atmosphere, hex, slot + 1));
    scene.add(avatar.root);
    return avatar;
  });

  const skins = new CreatureSkins(atmosphere);
  await Promise.all([...new Set(CAST.map((c) => c.type))].map((t) => skins.request(t)));
  const creatures: { type: string; body: SkinnedCreature; clip: string; duration: number }[] = [];
  for (const c of CAST) {
    if (!isAnimatedType(c.type) || !skins.ready(c.type)) continue;
    const look = creatureLook(c.type)!;
    // O corpo na superfície dos atores (F119) no tom do papel; a peça acesa, no núcleo neon.
    const body = skins.acquire(c.type, look.variants[0]!.id, { night: false, elite: false, tone: c.boss ? "chefe" : "inimigo" }, false);
    if (!body) continue;
    body.root.position.set(c.x, 0, c.z);
    scene.add(body.root);
    const clips = CREATURE_MODELS[c.type as keyof typeof CREATURE_MODELS].clips as Readonly<Record<string, { duration: number }>>;
    const clip = options.clip === "golpe" ? (Object.keys(clips).find((n) => n.startsWith("golpe:")) ?? "parado") : "parado";
    creatures.push({ type: c.type, body, clip, duration: clips[clip]?.duration ?? 1 });
  }

  // Habilidades em laço (F65): a onda do Brado (Baluarte) e a zona da Runa (Arcanista), a cada 2,5 s.
  // Os eventos de energia (pesquisa 11) acendem o chão da vitrine como na partida.
  const energy = new EnergyEvents(atmosphere);
  const vfx = new SkillVfx(scene, options.skills === true, energy);
  // Estilhaços de impacto (F68), em loop com as zonas.
  const shards = new Shards(scene);
  let nextVfx = 0;
  // A flecha da galeria (F92): a lança das peças como haste, voando pela curva, e a fita atrás.
  const arrow = options.skills ? { ribbon: new Ribbon(), mesh: new Mesh(SKILL_SHAPES.lanca, createSkillMeshMaterial(PALETTE.classes.patrulheiro.hex, 2.4, 0.3)), look: ribbonLook("flecha", PALETTE.classes.patrulheiro.hex, 1, false) } : undefined;
  if (arrow) {
    arrow.mesh.scale.setScalar(NOCK / 1.3);
    scene.add(arrow.ribbon.mesh, arrow.mesh);
  }
  const base = new Vector3();
  const tip = new Vector3();
  const dir = new Vector3();
  const along = new Quaternion();
  const point = new Vector3();
  /**
   * As fitas são refeitas a cada quadro pelos instantes da última vida até `t` (a vitrine não guarda passado):
   * com `&instante`, a captura sai igual; em laço, como no jogo.
   */
  const drawRibbons = (t: number) => {
    for (const s of swingers) {
      const loopStart = Math.floor(t / SWING_LOOP) * SWING_LOOP;
      const from = Math.max(loopStart, t - s.look.lifeMs / 1000);
      s.ribbon.begin(s.look);
      for (let k = 0; k <= RIBBON_SAMPLES; k++) {
        const tk = from + ((t - from) * k) / RIBBON_SAMPLES;
        s.avatar.showClip("golpe", tk - loopStart);
        s.held.updateWorldMatrix(true, false);
        swingEnds(s.held, base, tip);
        s.ribbon.sample(tk * 1000, base.x, base.y, base.z, tip.x, tip.y, tip.z);
      }
      s.ribbon.update(t * 1000);
    }
    if (!arrow) return;
    const u = t % ARROW.loop;
    const start = t - u;
    const flying = Math.min(u, ARROW.flight);
    const at = (tt: number) => point.copy(ARROW.from).addScaledVector(ARROW.velocity, tt).setY(ARROW.from.y + ARROW.velocity.y * tt - 0.5 * ARROW.gravity * tt * tt);
    const heading = (tt: number) => dir.set(ARROW.velocity.x, ARROW.velocity.y - ARROW.gravity * tt, ARROW.velocity.z).normalize();
    arrow.ribbon.begin(arrow.look);
    const from = Math.max(0, flying - arrow.look.lifeMs / 1000);
    for (let k = 0; k <= 12; k++) {
      const tt = from + ((flying - from) * k) / 12;
      sampleArrowRibbon(arrow.ribbon, (start + tt) * 1000, at(tt), heading(tt), point.distanceTo(ARROW.from), camera.position);
    }
    if (u > ARROW.flight) arrow.ribbon.release((start + ARROW.flight) * 1000);
    arrow.ribbon.update(t * 1000);
    arrow.mesh.visible = u <= ARROW.flight;
    // A lança das peças aponta em +y: a ponta dela na ponta da flecha.
    arrow.mesh.quaternion.copy(along.setFromUnitVectors(base.set(0, 1, 0), heading(flying)));
    arrow.mesh.position.copy(at(flying)).addScaledVector(dir, -NOCK);
  };
  camera.position.set(5.5, 3.2, -8.5);
  camera.lookAt(new Vector3(0, 1.4, 3));
  if (options.arrowView === "lado") {
    camera.position.set(0, ARROW.from.y + 0.2, -8);
    camera.lookAt(new Vector3(0, ARROW.from.y, -1.8));
  } else if (options.arrowView === "atirador") {
    camera.position.set(ARROW.from.x - 1.2, ARROW.from.y + 0.25, ARROW.from.z - 0.6);
    camera.lookAt(new Vector3(ARROW.from.x + 8, ARROW.from.y, ARROW.from.z + 1));
  }

  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  let elapsed = 0;
  let last = performance.now();
  const vertices = () => {
    let n = 0;
    scene.traverse((o) => {
      const mesh = o as Mesh;
      if (mesh.isMesh) n += mesh.geometry.getAttribute("position").count;
    });
    return n;
  };
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    elapsed += dt;
    const t = options.time ?? elapsed;
    // Relógio dos efeitos (revisão da Fase 12): com `&instante=` fixo, os efeitos usam um relógio
    // derivado do instante (constante a cada quadro) em vez do relógio real — a captura sai igual
    // sempre (anéis e energia parados a meia-vida), e a contagem de triângulos não depende de um
    // anel morrer entre uma página e outra.
    const clock = options.time !== undefined ? t * 1000 + 100_000 : now;
    drawRibbons(t);
    for (const hero of heroes) hero.showClip(swingers.some((s) => s.avatar === hero) ? "golpe" : options.clip, t % SWING_LOOP);
    for (const c of creatures) c.body.apply({ samples: [{ clip: c.clip, time: Math.min(t % c.duration, c.duration - 1e-3), weight: 1 }], lift: 0, roll: 0, pitch: 0, dissolve: 0, clip: c.clip });
    if (clock >= nextVfx) {
      nextVfx = clock + 2500;
      // Com o relógio fixo, os efeitos nascem 400 ms "atrás": a captura os pega a meia-vida.
      const born = options.time !== undefined ? clock - 400 : clock;
      vfx.spawn({ by: "", skill: "brado", x: -2.4, z: 0, radius: 6, ms: 900 }, PALETTE.classes.baluarte.hex, () => 0, born);
      vfx.spawn({ by: "", skill: "runa-de-contencao", x: 0, z: 5, radius: 4, ms: 2400 }, PALETTE.classes.arcanista.hex, () => 0, born);
      shards.burst({ x: 2.4, y: 1, z: 0, color: PALETTE.classes.lanceiro.hex, n: 24, speed: 4.5, size: 0.08, life: 0.6, up: 0.4 });
      shards.burst({ x: -1, y: 1, z: 3, color: PALETTE.inimigo.hex, n: 36, speed: 4.5, size: 0.11, life: 0.9, up: 0.55 });
      // Os jorros também nascem "400 ms atrás" com o instante fixo, em passos de 50 ms, e param ali (abaixo):
      // corriam no relógio real, e os triângulos dependiam de quantos quadros o motor rodou até a leitura.
      if (options.time !== undefined) for (let k = 0; k < 8; k++) shards.update(0.05);
      vfx.pulse(2.4, 0, 0, 1.4, PALETTE.classes.lanceiro.hex, "triangulos", born);
      if (options.skills) {
        vfx.spawn({ by: "", skill: "bastiao", x: -4, z: 6, radius: 3, ms: 2400 }, PALETTE.classes.baluarte.hex, () => 0, born);
        vfx.spawn({ by: "", skill: "salto-do-falcao", x: 3.5, z: 2, radius: 2.5, ms: 900 }, PALETTE.classes.lanceiro.hex, () => 0, born);
        vfx.spawn({ by: "", skill: "circulo-da-fenix", x: 2, z: 7, radius: 2.5, ms: 2400 }, PALETTE.classes.arcanista.hex, () => 0, born);
        vfx.spawn({ by: "", skill: "disparo-rompe-nucleo", x: -6, z: -1, x2: 6, z2: -1, radius: 1.2, ms: 600 }, PALETTE.classes.patrulheiro.hex, () => 0, born);
        vfx.column(0, 0, 9, PALETTE.classes.patrulheiro.hex, born);
      }
    }
    vfx.update(clock);
    energy.update(clock);
    shards.tether({ x: -2.4, y: 1, z: 0 }, { x: 0, y: 1, z: 5 }, PALETTE.classes.baluarte.hex);
    shards.update(options.time !== undefined ? 0 : dt);
    applyAtmosphere(atmosphere, sample, options.time === undefined ? elapsed : 0);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    hud.update(now, () => [`Eldrun · malha neon · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós) · programas ${countPrograms(renderer.info.programs).scene}`]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      frameCalls: counter.last.frameCalls,
      triangles: renderer.info.render.triangles,
      vertices: vertices(),
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      heroes: heroes.length,
      // Fitas desenhadas agora (F92, `&habilidades=1`).
      ribbons: [...swingers.map((s) => s.ribbon), ...(arrow ? [arrow.ribbon] : [])].filter((r) => r.mesh.visible).length,
      creatures: creatures.length,
      // Material de cada malha das criaturas (o papel neon).
      cast: creatures.map((c) => {
        const names: string[] = [];
        c.body.root.traverse((o) => (o as Mesh).isMesh && names.push(`${o.name}:${((o as Mesh).material as { name: string }).name}`));
        return { type: c.type, meshes: names };
      }),
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      for (const c of creatures) c.body.dispose();
      // Os heróis também (revisão da F119): a superfície deles segura geometria preparada e o registro do modo daltônico.
      for (const hero of heroes) hero.dispose();
      for (const s of swingers) s.ribbon.dispose();
      if (arrow) {
        arrow.ribbon.dispose();
        (arrow.mesh.material as { dispose(): void }).dispose();
      }
      skins.dispose();
      materials.dispose();
      hud.remove();
    },
  };
}
